/**
 * V2 yoink card — polaroid photo, handwritten title, ink-icon meta rows,
 * date stamp. Art direction: docs/concepts/atlas-mockup.png.
 */
import { escapeHtml, stripHtmlTags, truncate } from '../../assets/js/utils.js';
import { slugify } from '../../assets/js/shared/slugify.js';
import { imageSrc } from '../core/photo.js';
import { icon, categoryIcon } from './icons.js';
import { bravoCountHtml } from '../features/bravo/bravo.js';

/**
 * The embed clause that fills a yoink card, shared by every feed query
 * (Discover, Following, profile pages) so the card never misses a field.
 */
export const FEED_SELECT =
  '*,locations!inner(id,display_name,lat,lng,formatted_address,county,state_province,country,status),' +
  'public_profiles(id,username,full_name,avatar_url),' +
  'categories:category_id(id,name,emoji)';

export const FEED_PAGE_SIZE = 24;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function stampDate(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()} ’${String(d.getFullYear()).slice(2)}`;
}

function photoUrl(yoink) {
  const images = Array.isArray(yoink.images) ? yoink.images : [];
  if (!images.length || !images[0]) return null;
  return imageSrc(images[0]);
}

export const GRID_THUMB_WIDTH = 640;

/**
 * Grid thumbnail via Supabase's image-transform endpoint (assessment 1.4:
 * grids were shipping every full-size original — invisible today, the
 * bandwidth bill at scale). Swaps /object/public/ for
 * /render/image/public/ with a width+quality query. Transforms are a
 * paid-plan feature we can't detect from code, so every <img> using a
 * thumb must carry data-fallback-src with the ORIGINAL url — the
 * capturing error listener below retries it before the emoji
 * placeholder, making this safe on any plan. Detail views (yoink page,
 * fullscreen viewer) keep originals on purpose.
 */
export function thumbUrl(publicUrl, width = GRID_THUMB_WIDTH) {
  if (!publicUrl || !publicUrl.includes('/storage/v1/object/public/')) return publicUrl;
  const base = publicUrl.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  return `${base}${base.includes('?') ? '&' : '?'}width=${width}&quality=75`;
}

/**
 * Up to `cap` unique first-photos from feed rows (newest first) — the
 * splash-hero collages (category page, profile page) pull from rows
 * their pages already load, so the banner costs no extra query.
 */
export function heroPhotos(rows, cap = 5) {
  const out = [];
  for (const r of rows || []) {
    const img = Array.isArray(r.images) ? r.images[0] : null;
    if (!img) continue;
    const url = imageSrc(img);
    if (!out.includes(url)) {
      out.push(url);
      if (out.length >= cap) break;
    }
  }
  return out;
}

/**
 * Render one yoink card as an HTML string.
 * Interaction is handled by the grid container via event delegation on
 * data-* attributes (no per-card listeners). Keyboard/screen-reader entry
 * is the title (role=link) plus the location/category meta links; the
 * article itself is a mouse-only convenience target and must NOT be a
 * role=button/tabindex stop — an interactive element can't contain other
 * interactive elements (WCAG 4.1.2).
 */
/**
 * A 404'd photo must degrade to the category placeholder, not the
 * browser's broken-image glyph (V1 discovery.js:747-762). One capturing
 * listener covers every grid and log list, present and future: any img
 * carrying data-fallback-cat (the category NAME — the ink icon resolves
 * from it) swaps to its blank variant on error.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('error', (event) => {
    const img = event.target;
    if (!(img instanceof HTMLImageElement)) return;
    // A failed thumbnail (plan without image transforms, or a transform
    // hiccup) retries the ORIGINAL once before any placeholder
    const original = img.dataset.fallbackSrc;
    if (original && img.src !== original) {
      img.removeAttribute('data-fallback-src');
      img.src = original;
      return;
    }
    if (img.dataset.fallbackCat === undefined) return;
    const polaroid = img.closest('.polaroid');
    const mark = categoryIcon({ name: img.dataset.fallbackCat }, polaroid ? 44 : 22);
    if (polaroid) {
      polaroid.classList.add('polaroid-blank');
      polaroid.innerHTML = mark;
      return;
    }
    const blank = document.createElement('div');
    blank.className = `${img.className} log-thumb-blank`.trim();
    blank.innerHTML = mark;
    img.replaceWith(blank);
  }, true);
}

// The forward-looking warning on a discovery card: the yoink is a memory
// either way, but nobody should drive to the pin without knowing the
// place is gone (same stamps the list pages wear, list-page.js).
const CLOSED_BADGES = {
  closed_permanently: '<span class="card-closed">Permanently closed</span>',
  closed_temporarily: '<span class="card-closed card-closed-temp">Temporarily closed</span>',
};

export function yoinkCard(yoink) {
  const url = photoUrl(yoink);
  const userName = yoink.public_profiles?.full_name || yoink.public_profiles?.username || 'Anonymous';
  const locationName = yoink.locations?.display_name || '';
  const category = yoink.categories;
  const who = yoink.public_profiles?.username || ''; // shelf link needs the owner's handle
  const note = yoink.description ? truncate(stripHtmlTags(yoink.description), 90) : '';

  const photo = url
    ? `<div class="polaroid"><img src="${thumbUrl(url)}" alt="${escapeHtml(yoink.title || 'Yoink')}" loading="lazy"
        data-fallback-src="${url}" data-fallback-cat="${escapeHtml(category?.name || '')}"></div>`
    : `<div class="polaroid polaroid-blank">${categoryIcon(category, 44)}</div>`;

  return `
    <article class="v2-card" data-yoink-id="${yoink.id}">
      ${photo}
      <div class="card-stamp">Yoinked<small>${stampDate(yoink.created_at)}</small></div>
      <h3 class="card-title"><span role="link" tabindex="0"
        data-yoink-id="${yoink.id}">${escapeHtml(yoink.title || 'Untitled')}</span></h3>
      ${note ? `<p class="card-note">“${escapeHtml(note)}”</p>` : ''}
      <div class="card-meta">
        ${yoink.public_profiles?.username ? `
        <div class="meta-row meta-link" data-user-id="${yoink.user_id || ''}"
          data-username="${escapeHtml(yoink.public_profiles.username)}" role="link"
          tabindex="0" aria-label="View profile of ${escapeHtml(userName)}">
          ${icon('person')}<span>${escapeHtml(userName)}</span>
        </div>` : `
        <div class="meta-row" data-user-id="${yoink.user_id || ''}">
          ${icon('person')}<span>${escapeHtml(userName)}</span>
        </div>`}
        ${locationName ? `
        <div class="meta-row meta-link" data-location-id="${yoink.locations.id}" role="link"
          tabindex="0" aria-label="View location ${escapeHtml(locationName)}">
          ${icon('pin')}<span>${escapeHtml(locationName)}</span>
          ${CLOSED_BADGES[yoink.locations?.status] || ''}
        </div>` : ''}
        ${category?.name ? `
        <div class="meta-row meta-link" data-category-slug="${slugify(category.name)}" role="link"
          tabindex="0" aria-label="View category ${escapeHtml(category.name)}">
          ${categoryIcon(category)}<span>${escapeHtml(category.name)}</span>
        </div>` : ''}
        ${yoink.personalShelf?.name ? `
        <div class="meta-row${who ? ' meta-link' : ''}"${who ? `
          data-personal-owner="${escapeHtml(who)}" data-personal-slug="${escapeHtml(yoink.personalShelf.slug || '')}"
          role="link" tabindex="0" aria-label="View personal category ${escapeHtml(yoink.personalShelf.name)}"` : ''}>
          <span class="meta-emoji" aria-hidden="true">${yoink.personalShelf.emoji ? escapeHtml(yoink.personalShelf.emoji) : '🔒'}</span>
          <span>${escapeHtml(yoink.personalShelf.name)} <small class="card-personal-note">personal category</small></span>
        </div>` : ''}
      </div>
      ${bravoCountHtml(yoink.bravo_count)}
    </article>
  `;
}
